import { Box, Divider, TableSortLabel, Typography } from '@mui/material'
import { ExportProps, ReportsProps } from 'features/report/report-constants'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { isBuddhistYear, numberFormat, toBuddhistYear } from 'utils/app.utils'
/** TRANSLATION */
import { useTranslation } from 'react-i18next'

import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import EmptyData from 'features/report/component/emptyData'

/** STYLE */
import { colors } from 'constants/theme'
import * as UseStyled from 'features/report/useStyled'

import { Config } from 'config'
import React from 'react'
import { dateToView } from 'utils/date.utils'
import { UseSnCode } from 'utils/useSnCode'

export const dfLabConfig: ReportsProps = {
  key: 'DF_LAB',
  textName: 'DF_LAB',
  portrait: false,
  renderDataCustom: false,
  filter: {
    branch: true,
    date: true,
    rangeDate: false,
    prefix: false,
    receiptStatus: false,
    operativeType: false,
    billStatus: false,
    operativeCode: false,
    doctor: true,
    patient: false
  },
  export: {
    pdf: true,
    excel: true
  }
}

export interface FcComponent {
  page: number
  pageLimit: number
  rowCount: number
  sortBy: string
  sortType: string
  data: any
  onRequestSort: (sortBy: string, sortType: string) => void
  handleChangeRowsPerPage: (val: any) => void
  handleChangePage: (val: any) => void
  componentRef: any
  date: any
  filterTable: any
  display: any
  cover: any
  selectedDoctors?: { userId: number; fullname: string }[]
}

export default function DfLabList(props: FcComponent) {
  const { t } = useTranslation()
  const [snType, setSnType] = useState(false)
  const [indexDoc, setIndexDoc] = useState(0)
  const [doctors, setDoctors] = useState('')

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }

    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])


  const convertFormatDate = (formatDate: any) => {
    const yearBE = moment(formatDate).year() + isBuddhistYear()
    const formattedDate = moment(formatDate).format('MM/YY')
    const result = formattedDate + '/' + yearBE
    return dateToView(formatDate, true)
  }
  const convetFormatDate = convertFormatDate(props.date)

  const renderDfTable = (data: any): any => {
    const items = !_.isEmpty(data)
      ? _.chain([...data.rowDetail, ...data.rowDf])
        .groupBy('doctorId')
        .map((value: any, key: string) => {
          const doctorDetail = _.find(props.data?.rowDetail, { doctorId: Number(key) || value[0]?.doctorId })
          return {
            doctor: {
              fullName: _.get(doctorDetail, 'doctorFullname') || '',
              timeStart: _.get(doctorDetail, 'timeStart') || '',
              timeEnd: _.get(doctorDetail, 'timeEnd') || '',
              countAppointment: _.get(doctorDetail, 'countAppoitment') || '',
              countTreatment: _.get(doctorDetail, 'countTreatment') || '',
              handInsurance: _.get(doctorDetail, 'handInsurance') || ''
            },
            df: _.filter(value, (val) => {
              if (!_.has(val, 'lab') && _.has(val, 'operativeName')) return val
            })
          }
        })
        .value()
      : []
    return items
  }

  const sumTotal = (handInsurance: number, df: number, lab: number) => {
    if (df > handInsurance) {
      return numberFormat(df - lab)
    } else if (handInsurance > df) {
      return numberFormat(handInsurance - lab)
    } else {
      return 0
    }
  }

  function formatDate(dateString: string): string {
    const [day, month, year] = dateString.split('/');
    const buddhistYear = Number(year) - 543
    const dateBuddhist = moment(`${year}/${month}/${day}`).format('MMMM YYYY').replace(/YYYY/, buddhistYear.toString())
    return dateBuddhist
  }

  const renderDfMonthTable = (data: any): any => {
    const items = !_.isEmpty(data)
      ? _.chain([...data.rowDf])
        .groupBy('doctorId')
        .map((value: any, key: string) => {
          const doctor = _.find(props.data?.rowDf, { doctorId: Number(key) })
          return {
            doctor: {
              fullName: _.get(doctor, 'doctorFullname') || ''
            },
            df: value
          }
        })
        .value()
      : []
    return items
  }

  const renderCover = (data: any): any => {
    /** ใช้ list ของหมอที่ filter (props.selectedDoctors) เป็น source ของ cover pages
     *  เพื่อให้หมอที่ไม่มี df ใน cover.rowDf ก็ขึ้นใบปะหน้าด้วย (df = []) */
    if (props.selectedDoctors && props.selectedDoctors.length > 0) {
      const dfByDoctor = _.groupBy(data?.rowDf || [], 'doctorId')
      return props.selectedDoctors.map((doc) => ({
        doctor: { fullName: doc.fullname },
        df: dfByDoctor[doc.userId] || [],
      }))
    }
    const items = !_.isEmpty(data)
      ? _.chain([...data.rowDf])
        .groupBy('doctorId')
        .map((value: any, key: string) => {
          const doctor = _.find(props.cover?.rowDf, { doctorId: Number(key) })
          return {
            doctor: {
              fullName: _.get(doctor, 'doctorFullname') || ''
            },
            df: value
          }
        })
        .value()
      : []
    return items
  }

  const renderDfLabTable = (data: any) => {
    const items = !_.isEmpty(data)
      ? _.chain([...data.rowDetail, ...data.rowDf, ...data.rowLab])
        .groupBy('doctorId')
        .map((value: any, key: string) => {
          const doctorDetail = _.find(props.data?.rowDetail, { doctorId: Number(key) || value[0]?.doctorId })
          return {
            doctor: {
              fullName: _.get(doctorDetail, 'doctorFullname') || '',
              timeStart: _.get(doctorDetail, 'timeStart') || '',
              timeEnd: _.get(doctorDetail, 'timeEnd') || '',
              countAppointment: _.get(doctorDetail, 'countAppoitment') || '',
              countTreatment: _.get(doctorDetail, 'countTreatment') || '',
              handInsurance: _.get(doctorDetail, 'handInsurance') || ''
            },
            df: _.filter(value, (val) => {
              if (!_.has(val, 'lab') && _.has(val, 'operativeName')) return val
            }),
            lab: _.filter(value, (val) => {
              if (_.has(val, 'lab')) return val
            })
          }
        })
        .value()
      : []

    return items
  }
  const renderTimeWorking = (timeStart: string, timeEnd: string) => {
    return `${moment(timeStart, 'HH:mm').format('HH:mm')} - ${moment(timeEnd, 'HH:mm').format('HH:mm')} ${t('TIME_UNIT')}`
  }
  const headLabCells = [
    { id: 'B', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.#NO', align: 'center', class: 'text-nowrap', width: '30px' },
    { id: 'C', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.PATIENT_CN_NUMBER', align: 'left', class: 'text-nowrap', width: '80px' },
    { id: 'D', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.SN_CODE', align: 'left', class: 'text-nowrap', width: '80px' },
    { id: 'E', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.PATIENT_NAME', align: 'left', class: 'text-nowrap', width: '140px' },
    { id: 'F', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.TX_LAB', align: 'left', class: 'text-nowrap', width: '130px' },
    { id: 'G', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.LAB_LIST', align: 'left', class: 'text-nowrap', width: '150px' },
    { id: 'H', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.LAB_REMARK', align: 'left', class: 'text-nowrap', width: '140px' },
    { id: 'I', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.DATE_BILL', align: 'left', class: 'text-nowrap', width: '90px' },
    { id: 'J', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.PRICE', align: 'right', class: 'text-nowrap', width: '80px' },
    { id: 'K', disablePadding: false, labelKey: 'REPORT.TABLE.DF_LAB.PRICE_LAB', align: 'right', class: 'text-nowrap', width: '80px' }
  ]


  const headDfDIOCells = Config.IS_DIO !== '1' ? [{ id: 'I', disablePadding: false, labelKey: 'REPORT.TABLE.DF.IS_LAB', align: 'center', class: 'text-nowrap' }] : []

  const headDfCells = [
    { id: 'B', disablePadding: false, labelKey: 'REPORT.TABLE.DF.#NO', align: 'center', class: 'text-nowrap', width: '30px' },
    { id: 'C', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CN', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'D', disablePadding: false, labelKey: 'REPORT.TABLE.DF.BRANCH_CN', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'E', disablePadding: false, labelKey: 'REPORT.TABLE.DF.SN_CODE', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'F', disablePadding: false, labelKey: 'REPORT.TABLE.DF.PATIENT_NAME', align: 'center', class: 'text-nowrap', width: '120px' },
    { id: 'G', disablePadding: false, labelKey: 'REPORT.TABLE.DF.OPERATIVE_TYPE', align: 'center', class: 'text-nowrap', width: '100px' },
    { id: 'H', disablePadding: false, labelKey: 'REPORT.TABLE.DF.OPERATIVE_LIST', align: 'center', class: 'text-nowrap', width: '140px' },
    ...headDfDIOCells.map((c: any) => ({ ...c, width: '50px' })),
    { id: Config.IS_DIO !== '1' ? 'J' : 'I', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CLINIC_FEE', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: Config.IS_DIO !== '1' ? 'K' : 'J', disablePadding: false, labelKey: 'REPORT.TABLE.DF.QUANTITY', align: 'center', class: 'text-nowrap', width: '45px' },
    { id: Config.IS_DIO !== '1' ? 'L' : 'K', disablePadding: false, labelKey: 'REPORT.TABLE.DF.TRUE_PRICE', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: Config.IS_DIO !== '1' ? 'M' : 'L', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: Config.IS_DIO !== '1' ? 'N' : 'M', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF2', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: Config.IS_DIO !== '1' ? 'O' : 'N', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CREDIT_NET', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: Config.IS_DIO !== '1' ? 'P' : 'O', disablePadding: false, labelKey: 'REPORT.TABLE.DF.TREATMENT_NET', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: Config.IS_DIO !== '1' ? 'Q' : 'P', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF3', align: 'center', class: 'text-nowrap', width: '60px' }
  ]


  const headDfCellsMonth = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.#NO', align: 'center', class: 'text-nowrap', width: '30px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DATE', align: 'left', class: 'text-nowrap', width: '80px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.TIME', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.APPOINTMENT', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.WRONG_APPOINTMENT', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.WALK_IN', align: 'center', class: 'text-nowrap', width: '55px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.SUM_APPOINTMENT', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.PRICE', align: 'center', class: 'text-nowrap', width: '70px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CLINIC_FEE', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF4', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CREDIT_NET', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.TREATMENT_NET', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF5', align: 'center', class: 'text-nowrap', width: '60px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.HAND', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.LAB_COST', align: 'center', class: 'text-nowrap', width: '65px' },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF6', align: 'center', class: 'text-nowrap', width: '60px' }
  ]
  if (!snType) {
    headLabCells.splice(2, 1)
    headDfCells.splice(3, 1)
  }

  const headDfCover = [
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DATE', align: 'center', class: 'text-nowrap', fontSize: 16 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.PRICE', align: 'right', class: 'text-nowrap', fontSize: 16 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.CREDIT_NET', align: 'right', class: 'text-nowrap', fontSize: 16 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.SHIFT', align: 'right', class: 'text-nowrap', fontSize: 16 },
    { id: 'message', disablePadding: false, labelKey: 'REPORT.TABLE.DF.DF6', align: 'right', class: 'text-nowrap', fontSize: 16 }
  ]

  const renderDataDF = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    if (render === 'data') {
      const { isLab, cnNumber, branchCnNumber, snCode, patientFullname, operativeTypeName, operativeName, dfNet, creditCost, total, valueDf, df, treatmentRight, clinicFeePrice, qty } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const dataDF = Config.IS_DIO !== '1' ? [{ option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: isLab ? t('REPORT.UNIT.HAS') : t('REPORT.UNIT.NOT_HAS'), class: 'text-nowrap' },
      ] : []
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: num, class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: cnNumber || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: branchCnNumber || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: snCode || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: patientFullname || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: operativeTypeName || '-', class: 'text-nowrap' },
          {
            option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: <>
              {(operativeName || '-')}
              {(data?.isNhso === '1' ? <span style={{ color: '#2F4169', fontSize: exportData === true ? 7 : 12, fontWeight: "bold" }}>{` (สปสช.) `}</span> : '')}
            </>, class: 'text-nowrap'
          },
          ...dataDF,
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(clinicFeePrice || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(qty || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(total || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: valueDf, class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(df || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(creditCost || 0, 3, 3), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(treatmentRight || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(dfNet || 0, 3, 3), class: 'text-nowrap' }
        ]
      }
      if (!snType) {
        objRenderData.columns.splice(3, 1)
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'MERGE', align: 'center', colSpan: snType ? 7 : Config.IS_DIO !== '1' ? 6 : 5 },
          {
            option: 'TEXT',
            align: 'right',
            style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
            label: <Typography sx={{ fontWeight: 500, fontSize: exportData ? 10 : 16 }}>{t('REPORT.TITLE.SUM')}</Typography>,
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'clinicFeePrice') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'qty') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'total') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: '', class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'df') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'creditCost') || 0, 3, 3), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'treatmentRight') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'dfNet') || 0, 3, 3), class: 'text-nowrap' }
        ],
        classes: 'row-summary'
      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderDataLab = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    if (render === 'data') {
      const { branchCnNumber, snCode, patientFullname, operativeName, lab, remark, dateBill, labCost, labOrderPrice } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: num, class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: branchCnNumber || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: snCode || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: patientFullname || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: operativeName || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: lab || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: remark || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: toBuddhistYear(moment(dateBill), 'DD/MM/YYYY'), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(labOrderPrice || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(labCost || 0), class: 'text-nowrap' }
        ]
      }
      if (!snType) {
        objRenderData.columns.splice(2, 1)
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'MERGE', align: 'center', colSpan: snType ? 7 : 6 },
          {
            option: 'TEXT',
            align: 'right',
            style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
            label: <Typography sx={{ fontWeight: 500, fontSize: exportData ? 10 : 16 }}>{t('REPORT.TITLE.SUM')}</Typography>,
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'labOrderPrice') || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'labCost') || 0), class: 'text-nowrap' }
        ],
        classes: 'row-summary'
      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderDataDFMonth = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    try {
      if (render === 'data') {
        const { appointments, creditCost, date, df, clinicFeePrice, dfNet, handInsurance, notYet, timeEnd, timeStart, total, treatmentRight, labCost, walkIn } = data

        let workingTime = '-'

        if (timeStart && timeEnd) {
          const timeStartSplit = timeStart.split(':')
          const timeEndSplit = timeEnd.split(':')
          workingTime = timeStartSplit[0] + ':' + timeStartSplit[1] + ' - ' + timeEndSplit[0] + ':' + timeEndSplit[1]
        }
        const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
        const objRenderData = {
          key: num.toString(),
          id: no.toString(),
          obj: data,
          columns: [
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: num, class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: dateToView(date, true), class: 'text-nowrap' },
            {
              option: 'TEXT',
              align: 'center',
              style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
              label: workingTime,
              class: 'text-nowrap'
            },
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: appointments || '0', class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: notYet || '0', class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: walkIn || '0', class: 'text-nowrap' },
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: (walkIn || 0) + (appointments || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(total || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(clinicFeePrice || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(df || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(creditCost || 0, 3, 3), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(treatmentRight || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(dfNet || 0, 3, 3), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(handInsurance || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(labCost || 0), class: 'text-nowrap' },
            {
              option: 'TEXT',
              align: 'right',
              style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
              label: Number(dfNet) > Number(handInsurance) ? numberFormat(dfNet - labCost || 0, 3, 3) : numberFormat(Number(handInsurance) - Number(labCost || 0), 3, 3),
              class: 'text-nowrap'
            }
          ]
        }

        return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
      } else {
        const objRenderData = {
          key: no.toString(),
          id: no.toString(),
          obj: data,
          columns: [
            { option: 'MERGE', align: 'center', colSpan: 6 },
            {
              option: 'TEXT',
              align: 'right',
              style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
              label: <Typography sx={{ fontWeight: 500, fontSize: exportData ? 10 : 16 }}>{t('REPORT.TITLE.SUM')}</Typography>,
              class: 'text-nowrap'
            },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'total') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'clinicFeePrice') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'df') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'creditCost') || 0, 3, 3), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'treatmentRight') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'dfNet') || 0, 3, 3), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'handInsurance') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 }, label: numberFormat(_.sumBy(data, 'labCost') || 0), class: 'text-nowrap' },
            {
              option: 'TEXT',
              align: 'right',
              style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: exportData ? 10 : 16 },
              label: numberFormat(_.sumBy(data, (o: any) => (Number(o.dfNet) > Number(o.handInsurance) ? Number(o.dfNet) - Number(o.labCost) : Number(o.handInsurance) - Number(o.labCost))) || 0, 3, 3),
              class: 'text-nowrap'
            }
          ],
          classes: 'row-summary'
        }
        return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
      }
    } catch (error) {
      return <></>
    }
  }

  const renderDataCover = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    try {
      if (render === 'data') {
        const { appointments, creditCost, date, df, dfNet, handInsurance, notYet, timeEnd, timeStart, total, totalPrice, treatmentRight, labCost, walkIn } = data
        let workingTime = '-'

        if (timeStart && timeEnd) {
          const timeStartSplit = timeStart.split(':')
          const timeEndSplit = timeEnd.split(':')
          workingTime = timeStartSplit[0] + ':' + timeStartSplit[1] + ' - ' + timeEndSplit[0] + ':' + timeEndSplit[1]
        }
        const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
        const objRenderData = {
          key: num.toString(),
          id: no.toString(),
          obj: data,
          columns: [
            { option: 'TEXT', align: 'center', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 14 }, label: moment(date).add(isBuddhistYear(), 'y').format('DD/MM/YYYY'), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 14 }, label: numberFormat(total || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 14 }, label: numberFormat(creditCost || 0, 2, 2), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 14 }, label: numberFormat(handInsurance || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 14 }, label: numberFormat(dfNet || 0, 2, 2), class: 'text-nowrap' }
          ]
        }

        return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
      } else {
        const objRenderData = {
          key: no.toString(),
          id: no.toString(),
          obj: data,
          columns: [
            {
              option: 'TEXT',
              align: 'right',
              style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 16 },
              label: <Typography sx={{ fontWeight: 500, fontSize: 16 }}>{t('REPORT.TITLE.SUM')}</Typography>,
              class: 'text-nowrap'
            },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 16 }, label: numberFormat(_.sumBy(data, 'total') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 16 }, label: numberFormat(_.sumBy(data, 'creditCost') || 0, 2, 2), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 16 }, label: numberFormat(_.sumBy(data, 'handInsurance') || 0), class: 'text-nowrap' },
            { option: 'TEXT', align: 'right', style: { border: exportData ? `0px solid ${colors.black}` : '', fontSize: 16 }, label: numberFormat(_.sumBy(data, (o: any) => (o.dfNet)) || 0, 2, 2), class: 'text-nowrap' }
          ],
          classes: 'row-summary'
        }
        return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
      }
    } catch (error) {
      return <></>
    }
  }


  return (
    <>
      {((!_.isEmpty(props.data.rowDetail) || !_.isEmpty(props.data.rowDf) || !_.isEmpty(props.data.rowLab)) &&
        _.map(props.display === 'day' ? renderDfLabTable(props.data) : renderDfMonthTable(props.data), (data, index: number) => {
          if (props.display === 'day' ? data.doctor?.fullName : data.df.length > 0) {
            return (
              <Box key={index}>
                {index > 0 && <Divider className="mx-0 my-4" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />}
                <Typography className={`mb-1`} sx={{ fontWeight: 500, fontSize: '20px' }}>
                  <span className="pr-2">{t('REPORT.FILTER.DOCTOR')}</span>
                  {data.doctor.fullName || '-'}
                </Typography>
                {props.display === 'day' && (
                  <Box className="d-sm-flex">
                    <Box className="d-flex">
                      <>
                        <Typography className="mr-1" sx={{ fontWeight: 500 }}>
                          {t('REPORT.TITLE.DATE')}
                        </Typography>
                        <Typography sx={{ marginRight: '5px' }}>{convetFormatDate}</Typography>
                        <Typography className="mr-1" sx={{ fontWeight: 500 }}>
                          {t('REPORT.TITLE.TME_WORK')}
                        </Typography>
                        <Typography>{data.doctor.timeStart && data.doctor.timeEnd ? renderTimeWorking(data.doctor.timeStart, data.doctor.timeEnd) : '-'}</Typography>
                      </>
                    </Box>
                    <Box className="d-flex ml-2">
                      <Typography className="mr-1" sx={{ fontWeight: 500 }}>
                        {t('REPORT.TITLE.COUNT_APPOINTMENT')}
                      </Typography>
                      <Typography>{data.doctor.countAppointment ? `${data.doctor.countAppointment} ${t('REPORT.TITLE.APPOINTMENT_UNIT')}` : '-'}</Typography>
                    </Box>
                    <Box className="d-flex ml-2">
                      <Typography className="mr-1" sx={{ fontWeight: 500 }}>
                        {t('REPORT.TITLE.COUNT_TREATMENT')}
                      </Typography>
                      <Typography>{data.doctor.countTreatment ? `${data.doctor.countTreatment} ${t('REPORT.TITLE.PEOPLE_UNIT')}` : '-'} </Typography>
                    </Box>
                  </Box>
                )}

                <Box>
                  {props.display === 'day' && (
                    <UseStyled.ResponsiveReportTable className="mt-2" verticalAlign="top">
                      <TableCustom
                        customScroll
                        hidePagination
                        page={props.page}
                        pageLimit={props.pageLimit}
                        sortType={props.sortType}
                        sortBy={props.sortBy}
                        rowCount={props.rowCount}
                        onSort={props.onRequestSort}
                        setPageLimit={props.handleChangeRowsPerPage}
                        setPage={props.handleChangePage}
                        headCells={headDfCells}
                        rowsData={
                          !_.isEmpty(data.df)
                            ? [
                              _.map(data.df, (df: any, indexData: number) => {
                                if (df?.patientFullname) return renderDataDF(df, indexData, false, 'data')
                              }),
                              renderDataDF(data.df, 0, false, 'sum')
                            ]
                            : []
                        }
                      />
                    </UseStyled.ResponsiveReportTable>
                  )}

                  {props.display === 'month' && (
                    <UseStyled.ResponsiveReportTable className="mt-2" verticalAlign="top">
                      <TableCustom
                        customScroll
                        hidePagination
                        page={props.page}
                        pageLimit={props.pageLimit}
                        sortType={props.sortType}
                        sortBy={props.sortBy}
                        rowCount={props.rowCount}
                        onSort={props.onRequestSort}
                        setPageLimit={props.handleChangeRowsPerPage}
                        setPage={props.handleChangePage}
                        headCells={headDfCellsMonth}
                        rowsData={
                          !_.isEmpty(data.df)
                            ? [
                              _.map(data.df, (df: any, indexData: number) => {
                                return renderDataDFMonth(df, indexData, false, 'data')
                              }),
                              renderDataDFMonth(data.df, 0, false, 'sum')
                            ]
                            : []
                        }
                      />
                    </UseStyled.ResponsiveReportTable>
                  )}
                </Box>


                {(props.display === 'day' && Config.IS_DIO !== '1') && (
                  <Box>
                    <Typography className="mt-3" sx={{ fontWeight: 500, fontSize: '18px' }}>
                      {t('REPORT.TITLE.LAB')}
                    </Typography>
                    <UseStyled.ResponsiveReportTable className="mt-2" verticalAlign="top">
                      <TableCustom
                        customScroll
                        hidePagination
                        page={props.page}
                        pageLimit={props.pageLimit}
                        sortType={props.sortType}
                        sortBy={props.sortBy}
                        rowCount={props.rowCount}
                        onSort={props.onRequestSort}
                        setPageLimit={props.handleChangeRowsPerPage}
                        setPage={props.handleChangePage}
                        headCells={headLabCells}
                        rowsData={
                          !_.isEmpty(data.lab)
                            ? [
                              _.map(data.lab, (lab: any, indexData: number) => {
                                if (lab?.patientFullname) return renderDataLab(lab, indexData, false, 'data')
                              }),
                              renderDataLab(data.lab, 0, false, 'sum')
                            ]
                            : []
                        }
                      />
                    </UseStyled.ResponsiveReportTable>
                  </Box>
                )}

                {props.display === 'day' && (
                  <Box>
                    <Typography className="mt-3" sx={{ fontWeight: 500, fontSize: '18px' }}>
                      {t('REPORT.TITLE.SUMMARY')}
                    </Typography>
                    <Box>
                      <Box className="d-flex mt-2">
                        <Typography className="mr-1" sx={{ fontWeight: 500, width: '120px', flex: 'none' }}>
                          {t('REPORT.TABLE.DF_LAB.NET_DF')}
                        </Typography>
                        <Typography className="text-right mr-1" sx={{ width: '100px' }}>
                          {numberFormat(_.sumBy(data.df, 'dfNet') || 0)}
                        </Typography>
                        <Typography>{t('CURRENCY_CHANGE.TH')}</Typography>
                      </Box>
                      {Config.IS_DIO !== '1' && <Box className="d-flex mt-2">
                        <Typography className="mr-1" sx={{ fontWeight: 500, width: '120px', flex: 'none' }}>
                          {t('REPORT.TABLE.DF_LAB.PRICE_LAB')}
                        </Typography>
                        <Typography className="text-right mr-1" sx={{ width: '100px' }}>
                          {numberFormat(_.sumBy(data.lab, 'labCost') || 0)}
                        </Typography>
                        <Typography>{t('CURRENCY_CHANGE.TH')}</Typography>
                      </Box>}
                      <Box className="d-flex mt-2">
                        <Typography className="mr-1" sx={{ fontWeight: 500, width: '120px', flex: 'none' }}>
                          {t('REPORT.TITLE.HAND_INSURANCE')}
                        </Typography>
                        <Typography className="text-right mr-1" sx={{ width: '100px' }}>
                          {numberFormat(data.doctor.handInsurance || 0)}
                        </Typography>
                        <Typography>{t('CURRENCY_CHANGE.TH')}</Typography>
                      </Box>
                      <Box className="d-flex mt-2">
                        <Typography className="mr-1" sx={{ fontWeight: 500, width: '120px', flex: 'none' }}>
                          {t('REPORT.TITLE.NET_SUMMARY')}
                        </Typography>
                        <Typography className="text-right mr-1" sx={{ width: '100px' }}>
                          {sumTotal(data.doctor.handInsurance || 0, _.sumBy(data.df, 'dfNet') || 0, _.sumBy(data.lab, 'labCost') || 0)}
                        </Typography>
                        <Typography>{t('CURRENCY_CHANGE.TH')}</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )
          }
        })) || <EmptyData />}

      <div className="print-show" ref={props.componentRef[0]}>
        <DfLabExport
          componant={
            <Box sx={{ width: '1122.519685px', maxWidth: '1122.519685px' }}>
              <span style={{ fontWeight: 'bold' }}>{t(`REPORT.REPORT_NAME.DF_LAB`)}</span>
              <table>
                <tr>
                  {props.filterTable.map((filter: any, index: number) => (
                    <TableSortLabel key={index}>
                      <td className="mr-2" style={{ textAlign: 'left', fontSize: 10, fontWeight: 800 }}>
                        {t(filter.label)} :{' '}
                      </td>
                      <td style={{ textAlign: 'left', fontSize: 10, fontWeight: 500 }}>{filter.value}</td>
                    </TableSortLabel>
                  ))}
                </tr>
              </table>
              {_.map(props.display === 'day' ? renderDfLabTable(props.data) : renderDfMonthTable(props.data), (data, index: number) => {
                if (props.display === 'day' ? data.doctor?.fullName : data.df.length > 0) {
                  return (
                    <Box
                      key={index}
                      sx={{
                        pageBreakAfter: 'always',
                        breakAfter: 'page',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                      }}
                    >
                      {index > 0 && <Divider className="mx-0 my-4" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />}
                      <Typography className={`mb-1`} sx={{ fontWeight: 500, fontSize: 12 }}>
                        <span className="pr-2">{t('REPORT.FILTER.DOCTOR')}</span>
                        {data.doctor.fullName || '-'}
                      </Typography>

                      {props.display === 'day' && (
                        <table style={{ fontWeight: 300, fontSize: 10 }}>
                          <td className="mr-1">{t('REPORT.TITLE.TME_WORK')}</td>
                          <td>{data.doctor.timeStart && data.doctor.timeEnd ? renderTimeWorking(data.doctor.timeStart, data.doctor.timeEnd) : '-'}</td>

                          <td className="mr-1">{t('REPORT.TITLE.COUNT_APPOINTMENT')}</td>
                          <td>{data.doctor.countAppointment ? `${data.doctor.countAppointment} ${t('REPORT.TITLE.APPOINTMENT_UNIT')}` : '-'}</td>

                          <td className="mr-1">{t('REPORT.TITLE.COUNT_TREATMENT')}</td>
                          <td>{data.doctor.countTreatment ? `${data.doctor.countTreatment} ${t('REPORT.TITLE.PEOPLE_UNIT')}` : '-'} </td>
                        </table>
                      )}

                      <Box>
                        <UseStyled.Styles className="pt-3">
                          <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                            {props.display === 'day' && (
                              <thead>
                                <tr>
                                  {_.map(headDfCells, (val: any) => {
                                    return <td style={{ textAlign: val.align, fontSize: 8, fontWeight: 500, width: val.width }}> {t(val.labelKey)}</td>
                                  })}
                                </tr>
                              </thead>
                            )}
                            {props.display === 'day' && (
                              <tbody>
                                {data.df.length > 0 && [
                                  _.map(data.df, (df: any, indexData: number) => {
                                    if (df?.patientFullname) return renderDataDF(df, indexData, true, 'data')
                                  }),
                                  renderDataDF(data.df, 0, true, 'sum')
                                ]}
                                {!data.df.length && (
                                  <td colSpan={headDfCells.length} style={{ fontSize: 10 }}>
                                    {t('REPORT.TITLE.NO_DATA')}
                                  </td>
                                )}
                              </tbody>
                            )}

                            {props.display === 'month' && (
                              <thead>
                                <tr>
                                  {_.map(headDfCellsMonth, (val: any) => {
                                    return <td style={{ textAlign: val.align, fontSize: 10, fontWeight: 500, width: val.width }}> {t(val.labelKey)}</td>
                                  })}
                                </tr>
                              </thead>
                            )}
                            {props.display === 'month' && (
                              <tbody>
                                {data.df.length > 0 && [
                                  _.map(data.df, (df: any, indexData: number) => {
                                    return renderDataDFMonth(df, indexData, true, 'data')
                                  }),
                                  renderDataDFMonth(data.df, 0, true, 'sum')
                                ]}
                                {!data.df.length && (
                                  <td colSpan={headDfCells.length} style={{ fontSize: 10 }}>
                                    {t('REPORT.TITLE.NO_DATA')}
                                  </td>
                                )}
                              </tbody>
                            )}
                          </table>

                          <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%', marginTop: '10px' }}>
                            {(props.display === 'day' && Config.IS_DIO !== '1') && (
                              <thead>
                                <tr>
                                  {_.map(headLabCells, (val: any) => {
                                    return <td style={{ textAlign: val.align, fontSize: 10, fontWeight: 500, width: val.width }}> {t(val.labelKey)}</td>
                                  })}
                                </tr>
                              </thead>
                            )}

                            {(props.display === 'day' && Config.IS_DIO !== '1') && (
                              <tbody>
                                {data.lab?.length > 0 && [
                                  _.map(data.lab, (lab: any, indexData: number) => {
                                    if (lab?.patientFullname) return renderDataLab(lab, indexData, true, 'data')
                                  }),
                                  renderDataLab(data.lab, 0, true, 'sum')
                                ]}
                                {!data.lab?.length && (
                                  <td colSpan={headLabCells.length} style={{ fontSize: 10 }}>
                                    {t('REPORT.TITLE.NO_DATA')}
                                  </td>
                                )}
                              </tbody>
                            )}
                          </table>
                        </UseStyled.Styles>
                        {props.display === 'day' && (
                          <div className="Box">
                            <Typography className="mt-3" sx={{ fontWeight: 300, fontSize: 10 }}>
                              {t('REPORT.TITLE.SUMMARY')}
                            </Typography>
                            <table style={{ fontSize: 12 }}>
                              <td className="mr-1">{t('REPORT.TABLE.DF_LAB.NET_DF')}</td>
                              <td className="text-right mr-1">{numberFormat(_.sumBy(data.df, 'dfNet') || 0)}</td>
                              <td>{t('CURRENCY_CHANGE.TH')}</td>
                              {Config.IS_DIO !== '1' && (<><tr></tr>
                                <td className="mr-1">{t('REPORT.TABLE.DF_LAB.PRICE_LAB')}</td>
                                <td className="text-right mr-1">{numberFormat(_.sumBy(data.lab, 'labCost') || 0)}</td>
                                <td>{t('CURRENCY_CHANGE.TH')}</td></>)}
                              <tr></tr>
                              <td className="mr-1">{t('REPORT.TITLE.HAND_INSURANCE')}</td>
                              <td className="text-right mr-1">{numberFormat(data.doctor.handInsurance || 0)}</td>
                              <td>{t('CURRENCY_CHANGE.TH')}</td>
                              <tr></tr>
                              <td className="mr-1">{t('REPORT.TITLE.NET_SUMMARY')}</td>
                              <td className="text-right mr-1">{sumTotal(data.doctor.handInsurance || 0, _.sumBy(data.df, 'dfNet') || 0, _.sumBy(data.lab, 'labCost') || 0)} </td>
                              <td>{t('CURRENCY_CHANGE.TH')}</td>
                            </table>
                          </div>
                        )}
                      </Box>
                    </Box>
                  )
                }
              })}
            </Box>
          }
        />
      </div>

      <div className="print-show" ref={props.componentRef[1]}>
        <DfLabExport
          componant={
            ((props.selectedDoctors?.length ?? 0) > 0 || !_.isEmpty(props.cover.rowDetail) || !_.isEmpty(props.cover.rowDf) || !_.isEmpty(props.cover.rowLab)) ?
             <Box >
                {_.map(renderCover(props.cover), (data, index: number) => {
                  /** Render ทุกหมอที่ filter — หมอไม่มี df ขึ้นใบปะหน้าว่าง (NO_DATA) */
                  return (
                    <Box
                      key={index}
                      sx={{
                        minHeight: 'calc(1122.519685px - 1cm - 40px)',
                        display: 'flex',
                        flexDirection: 'column',
                        ...(index > 0 && { pageBreakBefore: 'always', breakBefore: 'page' }),
                      }}
                      className={`df-doctor-${index}`}
                    >
                      {/* Top section — เนื้อหาหลัก (header + ชื่อหมอ + ตาราง) ขยายเต็มพื้นที่ */}
                      <Box sx={{ flex: 1 }} className={`df-doctor-${index}`}>
                        <Box>
                          <span style={{ fontWeight: 'bold' }}>{t(`REPORT.REPORT_NAME.DF_LAB`)}</span>
                          <table>
                            <tr>
                              {props.filterTable.map((filter: any, index: number) => (
                                <TableSortLabel key={index}>
                                  {filter.label !== t('REPORT.FILTER.DOCTOR') && (
                                    <Box>
                                      <td className="mr-2" style={{ textAlign: 'left', fontSize: 16, fontWeight: 800 }}>
                                        {t(filter.label)} :{' '}
                                      </td>
                                      <td style={{ textAlign: 'left', fontSize: 16, fontWeight: 500 }}>{filter.value}</td>
                                    </Box>
                                  )}
                                </TableSortLabel>
                              ))}
                            </tr>
                          </table>
                        </Box>
                        <Typography className={`mb-1`} sx={{ fontWeight: 500, fontSize: 16 }}>
                          <span className="pr-2">{t('REPORT.FILTER.DOCTOR')}</span>
                          {data.doctor.fullName || '-'}
                        </Typography>
                        <Box>
                          <UseStyled.Styles className="pt-3">
                            <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                              <thead>
                                <tr>
                                  {_.map(headDfCover, (val: any) => {
                                    return <td style={{ textAlign: val.align, fontSize: 16, fontWeight: 500 }}> {t(val.labelKey)}</td>
                                  })}
                                </tr>
                              </thead>

                              <tbody>
                                {data.df.length > 0 && [
                                  _.map(data.df, (df: any, indexData: number) => {
                                    return renderDataCover(df, indexData, true, 'data')
                                  }),
                                  renderDataCover(data.df, 0, true, 'sum')
                                ]}
                                {!data.df.length && (
                                  <td colSpan={headDfCover.length} style={{ fontSize: 16 }}>
                                    {t('REPORT.TITLE.NO_DATA')}
                                  </td>
                                )}
                              </tbody>
                            </table>
                          </UseStyled.Styles>
                        </Box>
                      </Box>
                      {/* Footer — สรุป DF / ตัด LAB / ค่าประกันเวร / DF สุทธิ + ลายเซ็น ติดก้นหน้า */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '54%', marginLeft: '67px' }}>
                          <Box sx={{ textAlign: 'left' }}>
                            <Box>{t('REPORT.EXPORT.NET_DF')}</Box>
                            <Box>{t('REPORT.EXPORT.PRICE_LAB')}</Box>
                            <Box>{t('REPORT.EXPORT.HAND_INSURANCE')}</Box>
                            <Box>{t('REPORT.EXPORT.DF_SUMMARY')}</Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Box>
                              {numberFormat(_.sumBy(data.df, 'dfNet'))} {t('CURRENCY_CHANGE.TH')}
                            </Box>
                            <Box>
                              {numberFormat(_.sumBy(data.df, 'labCost') || 0)} {t('CURRENCY_CHANGE.TH')}
                            </Box>
                            <Box>
                              {numberFormat(_.sumBy(data.df, 'handInsurance') || 0)} {t('CURRENCY_CHANGE.TH')}
                            </Box>
                            <Box>
                              {numberFormat(_.sumBy(data.df, (o: any) => (o.dfNet > o.handInsurance ? o.dfNet - o.labCost : o.handInsurance - o.labCost)) || 0)} {t('CURRENCY_CHANGE.TH')}
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ width: '100%', background: 'white', display: 'flex', justifyContent: 'space-between', padding: '0px 10px 0px 10px', marginTop: '20px' }}>
                          <Box sx={{ textAlign: 'left' }}>
                            <Box>ลงชื่อ............................................ผู้จัดทำ</Box>
                            <Box sx={{ marginLeft: '30px' }}>(............................................)</Box>
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Box>ลงชื่อ............................................ผู้รับเงิน</Box>
                            <Box sx={{ marginLeft: '-10px', textAlign: 'center' }}>({data.doctor.fullName})</Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )
                })}
              </Box> :
              <Box >
                {_.map(props.filterTable[2].value, (data, index: number) => {
                  if (data.length > 0) {
                    return (
                      <Box
                        key={index}
                        sx={{
                          minHeight: 'calc(1122.519685px - 1cm - 40px)',
                          display: 'flex',
                          flexDirection: 'column',
                          ...(index > 0 && { pageBreakBefore: 'always', breakBefore: 'page' }),
                        }}
                        className={`df-doctor-${index}`}
                      >
                        <Box sx={{ flex: 1 }} className={`df-doctor-${index}`}>
                          <Box>
                            <span style={{ fontWeight: 'bold' }}>{t(`REPORT.REPORT_NAME.DF_LAB`)}</span>
                            <table>
                              <tr>
                                {props.filterTable.map((filter: any, index: number) => (
                                  <TableSortLabel key={index} >
                                    {
                                      filter.label !== t('REPORT.FILTER.DOCTOR') && (
                                        <Box>
                                          <td className="mr-2" style={{ textAlign: 'left', fontSize: 16, fontWeight: 800 }}>
                                            {t(filter.label)} :{' '}
                                          </td>
                                          <td style={{ textAlign: 'left', fontSize: 16, fontWeight: 500 }}>{filter.label === t('REPORT.FILTER.MONTH') ? formatDate(filter.value) : filter.value}</td>
                                        </Box>
                                      )
                                    }
                                  </TableSortLabel>
                                ))}
                              </tr>
                            </table>
                          </Box>
                          <Typography className={`mb-1`} sx={{ fontWeight: 500, fontSize: 16 }}>
                            <span className="pr-2">{t('REPORT.FILTER.DOCTOR')}</span>
                            {data || '-'}
                          </Typography>
                          <Box>
                            <UseStyled.Styles className="pt-3">
                              <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                                <thead>
                                  <tr>
                                    {_.map(headDfCover, (val: any) => {
                                      return <td style={{ textAlign: val.align, fontSize: 16, fontWeight: 500 }}> {t(val.labelKey)}</td>
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                </tbody>
                              </table>
                            </UseStyled.Styles>
                          </Box>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '54%', marginLeft: '67px' }}>
                            <Box sx={{ textAlign: 'left' }}>
                              <Box>{t('REPORT.EXPORT.NET_DF')}</Box>
                              <Box>{t('REPORT.EXPORT.PRICE_LAB')}</Box>
                              <Box>{t('REPORT.EXPORT.HAND_INSURANCE')}</Box>
                              <Box>{t('REPORT.EXPORT.DF_SUMMARY')}</Box>
                            </Box>
                          </Box>
                          <Box sx={{ width: '100%', background: 'white', display: 'flex', justifyContent: 'space-between', padding: '0px 10px 0px 10px', marginTop: '20px' }}>
                            <Box sx={{ textAlign: 'left' }}>
                              <Box>ลงชื่อ............................................ผู้จัดทำ</Box>
                              <Box sx={{ marginLeft: '30px' }}>(............................................)</Box>
                            </Box>
                            <Box sx={{ textAlign: 'left' }}>
                              <Box>ลงชื่อ............................................ผู้รับเงิน</Box>
                              <Box sx={{ marginLeft: '-10px', textAlign: 'center' }}>({data})</Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )
                  }
                })}
              </Box>
          }
        />
      </div >

      <div id="print-table-container" ref={props.componentRef[2]} style={{ overflowX: 'hidden', padding: 0 }}>
        <DfLabExport
          headCells={t(`REPORT.REPORT_NAME.DF_LAB`)}
          componant={
            <>
              <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                <tr><th style={{ textAlign: 'start', fontSize: 14, fontWeight: 600 }}>{t(`REPORT.REPORT_NAME.DF_LAB`)}</th></tr>
                <tr>
                  {props.filterTable.map((filter: any, index: number) => (
                    <React.Fragment key={index}>
                      <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 500 }}>{t(filter.label)}</th>
                      <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 500 }}>{filter.value}</th>
                    </React.Fragment>
                  ))}
                </tr>
                <tr></tr>
                {_.map(props.display === 'day' ? renderDfLabTable(props.data) : renderDfMonthTable(props.data), (data, index: number) => {
                  if (props.display === 'day' ? data.doctor?.fullName : data.df.length > 0) {
                    return (
                      <>
                        <tr>
                          <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 500 }}>{t('REPORT.FILTER.DOCTOR')}</th>
                          <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 500 }}>{data.doctor.fullName || '-'}</th>
                        </tr>
                        {props.display === 'day' && (
                          <tr>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{t('REPORT.TITLE.TME_WORK')}</th>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{data.doctor.timeStart && data.doctor.timeEnd ? renderTimeWorking(data.doctor.timeStart, data.doctor.timeEnd) : '-'}</th>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{t('REPORT.TITLE.COUNT_APPOINTMENT')}</th>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{data.doctor.countAppointment ? `${data.doctor.countAppointment} ${t('REPORT.TITLE.APPOINTMENT_UNIT')}` : '-'}</th>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{t('REPORT.TITLE.COUNT_TREATMENT')}</th>
                            <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 300 }}>{data.doctor.countTreatment ? `${data.doctor.countTreatment} ${t('REPORT.TITLE.PEOPLE_UNIT')}` : '-'} </th>
                          </tr>
                        )}
                        {props.display === 'day' && (
                          <thead>
                            <tr>
                              {_.map(headDfCells, (val: any, index: number) => {
                                return <th style={{ textAlign: val.align, fontSize: 10, fontWeight: 500 }}> {t(val.labelKey)}</th>
                              })}
                            </tr>
                          </thead>)}
                        {props.display === 'day' && (
                          <tbody>
                            {data.df.length > 0 && [
                              _.map(data.df, (df: any, indexData: number) => {
                                if (df?.patientFullname) return renderDataDF(df, indexData, true, 'data')
                              }),
                              renderDataDF(data.df, 0, true, 'sum')
                            ]}
                            {!data.df.length && (
                              <tr><th colSpan={headDfCells.length} style={{ textAlign: 'center', fontSize: 10, fontWeight: 500 }}> {t('REPORT.TITLE.NO_DATA')}</th></tr>
                            )}
                          </tbody>
                        )}
                        <tr></tr>
                        {props.display === 'month' && (<thead>
                          <tr>
                            {_.map(headDfCellsMonth, (val: any) => {
                              return <th style={{ textAlign: val.align, fontSize: 10, fontWeight: 500 }}> {t(val.labelKey)}</th>
                            })}
                          </tr>
                        </thead>)}
                        {props.display === 'month' && (
                          <tbody>
                            {data.df.length > 0 && [
                              _.map(data.df, (df: any, indexData: number) => {
                                return renderDataDFMonth(df, indexData, true, 'data')
                              }),
                              renderDataDFMonth(data.df, 0, true, 'sum')
                            ]}
                            {!data.df.length && (
                              <tr><th colSpan={headDfCellsMonth.length} style={{ textAlign: 'center', verticalAlign: 'middle', fontSize: 10, fontWeight: 500 }}> {t('REPORT.TITLE.NO_DATA')}</th></tr>
                            )}
                          </tbody>
                        )}
                        <table style={{ tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: '100%', maxWidth: '100%' }}>
                          {(props.display === 'day' && Config.IS_DIO !== '1') && (
                            <thead>
                              <tr>
                                {_.map(headLabCells, (val: any) => {
                                  return <th style={{ textAlign: val.align, fontSize: 10, fontWeight: 500 }}> {t(val.labelKey)}</th>
                                })}
                              </tr>
                            </thead>
                          )}
                          {(props.display === 'day' && Config.IS_DIO !== '1') && (
                            <tbody>
                              {data.lab?.length > 0 && [
                                _.map(data.lab, (lab: any, indexData: number) => {
                                  if (lab?.patientFullname) return renderDataLab(lab, indexData, true, 'data')
                                }),
                                renderDataLab(data.lab, 0, true, 'sum')
                              ]}
                              {!data.lab?.length && (
                                <tr><th colSpan={headLabCells.length} style={{ textAlign: 'center', fontSize: 10, fontWeight: 500 }}> {t('REPORT.TITLE.NO_DATA')}</th></tr>
                              )}
                            </tbody>
                          )}
                        </table >
                        {props.display === 'day' && (
                          <>
                            <table style={{ fontSize: 12 }}>
                              <tr>
                                <th style={{ textAlign: 'start', fontSize: 10, fontWeight: 500 }}>{t('REPORT.TITLE.SUMMARY')}</th>
                              </tr>
                              <tr>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('REPORT.TABLE.DF_LAB.NET_DF')}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{numberFormat(_.sumBy(data.df, 'dfNet') || 0)}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('CURRENCY_CHANGE.TH')}</th>
                              </tr>
                              {Config.IS_DIO !== '1' && (<tr>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('REPORT.TABLE.DF_LAB.PRICE_LAB')}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{numberFormat(_.sumBy(data.lab, 'labCost') || 0)}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('CURRENCY_CHANGE.TH')}</th></tr>)}
                              <tr>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('REPORT.TITLE.HAND_INSURANCE')}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{numberFormat(data.doctor.handInsurance || 0)}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('CURRENCY_CHANGE.TH')}</th>
                              </tr>
                              <tr>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('REPORT.TITLE.NET_SUMMARY')}</th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{sumTotal(data.doctor.handInsurance || 0, _.sumBy(data.df, 'dfNet') || 0, _.sumBy(data.lab, 'labCost') || 0)} </th>
                                <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 400 }}>{t('CURRENCY_CHANGE.TH')}</th>
                              </tr>
                            </table>
                          </>
                        )}
                      </>
                    )
                  }
                })}

              </table>
            </>
          }
        />
      </div >
    </>
  )
}


export function DfLabExport(props: ExportProps) {
  return (
    <>
      {props.headCells != null && props.headCells !== '' && (
        <div className="large" style={{ textAlign: 'left', fontSize: 10, fontWeight: 700 }}>{props.headCells}</div>
      )}
      {props.componant}
    </>
  )
}